// @file Surface remake panel store

import { trackEvent } from '@@/bits/analytics'
import { captureException } from '@@/bits/error_tracker'
import { getVuexStore } from '@@/bits/pinia'
import { displayNameForUser } from '@@/bits/user_model'
import { LibraryType } from '@@/enums'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceSharePanelStore } from '@@/pinia/surface_share_panel'
import PadletApi from '@@/surface/padlet_api'
import type { Library, LibraryAccess, User } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfaceRemakePanelStore = defineStore('surfaceRemakePanelStore', () => {
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
  const surfaceSharePanelStore = useSurfaceSharePanelStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */
  const surfaceVuexStore = getVuexStore<RootState>()

  const user = surfaceCurrentUserStore.currentUser as User
  const isLibraryWall = computed((): boolean => surfaceVuexStore?.getters.isLibraryWall)
  const isBuilderCurrentUser = computed((): boolean => surfaceVuexStore?.getters.isBuilderCurrentUser)
  const wallInCreatableLibraries = computed((): boolean => surfaceVuexStore?.getters.wallInCreatableLibraries)
  const wall = computed(() => surfaceVuexStore?.getters.wall)
  const canICreateWall = computed((): boolean => user.quota.can_make)
  const canIAdminister = computed((): boolean => surfacePermissionsStore.canIAdminister)
  const copyWallpaper = ref<boolean>(true)
  const copyPosts = ref<boolean>(false)
  const copyComments = ref<boolean>(false)
  const copyReactions = ref<boolean>(false)
  const copyAuthorship = ref<boolean>(false)
  const copyPeopleAndPrivacy = ref<boolean>(false)
  const privacyType = computed((): string => surfaceSharePanelStore.privacyType)
  const hasCollaborators = computed((): boolean => surfaceSharePanelStore.collaborators.length > 0)

  const xRemakePanel = ref<boolean>(false)
  const xLibrarySelectionMenu = ref<boolean>(false)

  const currentVisibleAndCreatableLibraries = computed(
    (): Library[] => surfaceVuexStore?.getters.currentVisibleAndCreatableLibraries,
  )

  const librarySelected = ref<Library | null>(null)
  const defaultLibrary = computed((): Library => surfaceVuexStore?.getters.defaultLibrary)
  const currentLibrary = computed((): Partial<Library> => {
    return {
      name: librarySelected.value != null ? librarySelected.value.name : displayNameForUser(user),
      avatar: librarySelected.value != null ? librarySelected.value?.avatar : user.avatar,
      libraryType: librarySelected.value != null ? librarySelected.value.libraryType : LibraryType.Solo,
      libraryAccess: librarySelected.value != null ? librarySelected.value.libraryAccess : emptyLibraryAccess,
    }
  })

  const titleInput = ref<string>('')

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */
  const showRemakePanel = (): void => {
    xRemakePanel.value = true
  }

  const closeRemakePanel = (): void => {
    xRemakePanel.value = false
  }

  function toggleLibrarySelectionMenu(): void {
    xLibrarySelectionMenu.value = !xLibrarySelectionMenu.value
  }

  function closeLibrarySelectionMenu(): void {
    xLibrarySelectionMenu.value = false
  }

  function updateLibrarySelected(payload: { librarySelected: Library; user: User }): void {
    librarySelected.value = payload.librarySelected
    xLibrarySelectionMenu.value = !xLibrarySelectionMenu.value
  }

  async function initializeStore(): Promise<void> {
    await Promise.all([
      getVuexStore()?.dispatch('fetchUserLibrarySettings'),
      getVuexStore()?.dispatch('fetchCreatableLibraries', { userId: user.id }),
      canIAdminister.value && surfaceSharePanelStore.fetchWallAccessSettings(),
    ])
  }

  function initializeSelectedLibrary(): void {
    if (currentVisibleAndCreatableLibraries.value !== null && defaultLibrary.value !== null) {
      const wallInPersonalLibrary: boolean = !isLibraryWall.value && isBuilderCurrentUser.value
      if (!wallInPersonalLibrary && wallInCreatableLibraries.value) {
        librarySelected.value = currentVisibleAndCreatableLibraries.value.find(
          (library) => library.id === wall.value?.library_id,
        ) as Library
      } else {
        const defaultLibraryId: number = Number((defaultLibrary.value as any).libraryId)
        librarySelected.value = currentVisibleAndCreatableLibraries.value.find(
          (library) => library.id === defaultLibraryId,
        ) as Library
      }
    }
  }

  async function remakeWall(): Promise<void> {
    if (!canICreateWall.value && librarySelected.value == null) {
      usePersonalPlansStore().quotaTriggeredUpgrade({
        user,
      })
      return
    }

    try {
      const data = await PadletApi.RemakePanel.fetch(wall.value.id, {
        title: titleInput.value,
        with_posts: copyPosts.value,
        with_comments: copyComments.value,
        with_reactions: copyReactions.value,
        with_same_privacy: copyPeopleAndPrivacy.value,
        with_original_authors: copyAuthorship.value,
        library_id: librarySelected.value?.id,
        copy_wallpaper: copyWallpaper.value,
      })
      trackEvent('Surface', 'Remade padlet', null, null, { sourceWallId: wall.value.id })
      window.location.href = data.next
    } catch (error) {
      captureException(error)
    }
  }

  function toggleCopyWallpaper(): void {
    copyWallpaper.value = !copyWallpaper.value
  }

  function toggleCopyPosts(): void {
    copyPosts.value = !copyPosts.value
  }

  function toggleCopyComments(): void {
    copyComments.value = !copyComments.value
  }

  function toggleCopyReactions(): void {
    copyReactions.value = !copyReactions.value
  }

  function toggleCopyAuthorship(): void {
    copyAuthorship.value = !copyAuthorship.value
  }

  function toggleCopyPeopleAndPrivacy(): void {
    copyPeopleAndPrivacy.value = !copyPeopleAndPrivacy.value
  }

  /* ---------------------- */
  /* CONSTANTS                */
  /* ---------------------- */
  const emptyLibraryAccess: LibraryAccess = {
    canViewLibraryInfo: false,
    canUpdateLibraryInfo: false,
    canUpdatePermissions: false,
    canViewLibraryMemberships: false,
    canViewLibraryAnalytics: false,
    canManageMembers: false,
    canManageBilling: false,
    canTrashLibrary: false,
    canUseLibraryAsOwner: false,
    canUseLibraryAsAdmin: false,
    canUseLibraryAsTeacher: false,
    canViewLibrarySecurity: false,
    canInviteSchoolAdmins: false,
    canInviteSchoolTeachers: false,
    canInviteSchoolStudents: false,
    canInviteMembers: false,
    canViewGallery: false,
    canManageContentSafetySettings: false,
  }

  return {
    user,
    wall,
    xRemakePanel,
    xLibrarySelectionMenu,
    currentVisibleAndCreatableLibraries,
    librarySelected,
    currentLibrary,
    titleInput,
    copyWallpaper,
    copyPosts,
    copyComments,
    copyReactions,
    copyAuthorship,
    copyPeopleAndPrivacy,
    privacyType,
    hasCollaborators,
    canIAdminister,
    showRemakePanel,
    closeRemakePanel,
    toggleLibrarySelectionMenu,
    updateLibrarySelected,
    closeLibrarySelectionMenu,
    initializeStore,
    initializeSelectedLibrary,
    remakeWall,
    toggleCopyWallpaper,
    toggleCopyPosts,
    toggleCopyComments,
    toggleCopyReactions,
    toggleCopyAuthorship,
    toggleCopyPeopleAndPrivacy,
  }
})
